<script setup>
/**
     VuPanel - A Vue 3 slide out panel component
    
    This component provides a customizable slide-out panel that can display one or more sections.
    It supports keyboard navigation, touch gestures, animations, and various styling options.
    
    Features:
    - Single or multiple panel support
    - Smooth slide animations with custom easing
    - Keyboard navigation (ESC to close)
    - Touch gesture support for mobile
    - Blackout overlay with click-to-close
    - Customizable styling and positioning
    - Confirm dialog support before closing
    - Responsive design
    
    Based on: https://lyollix.github.io/vue-slideout-panel-demo/
 */

import {onBeforeMount, onUnmounted, reactive, ref, watch} from 'vue'
import {getRoutePath, State, go} from '@/paks/vu-app'

// Animation configuration constants
const easeOutCubic = 'cubic-bezier(0.215, 0.61, 0.355, 1)' // Smooth cubic easing
const easeOutSine = 'cubic-bezier(0.39, 0.575, 0.565, 1)' // Sine easing for different effects
const Animation = easeOutCubic // Default animation curve
const Timeout = '0.8s' // Default animation duration

// Component props definition
const props = defineProps({
    // HTML content for the close button
    closeHtml: {type: String, default: ''},

    // HTML content for the close all button (dual panel mode)
    closeAllHtml: {type: String, default: ''},

    // HTML content for the extra/expand button
    extraHtml: {type: String, default: 'Extra show'},

    // Confirmation function called before closing
    confirm: {},

    // Number of panels to display
    count: {
        type: Number,
        default: 1,
        validator(v) {
            return v > 0 && v <= 2
        },
    },

    // Whether this is a help panel (affects behavior)
    isHelp: {type: Boolean, default: false},

    // Enable keyboard navigation (ESC to close)
    keyboard: {type: Boolean, default: true},

    // Controls visibility of the panel
    modelValue: {type: Boolean, default: false, required: true},

    // Whether this is a nested panel (affects behavior - disables updating app.back)
    nested: {type: Boolean, default: false},

    // Show extra content/panel
    showExtra: {type: Boolean, default: false},

    // Array of widths for panels (px, %, or CSS grid classes)
    widths: {
        type: Array,
        default: ['700px'],
        validator(v) {
            return v.reduce((a, b) => a && /col-(\w+-)?\d+|\d+px|\d+%/.test(b), true)
        },
    },

    // Array of CSS classes for different panel elements
    classes: {
        type: Array,
        default() {
            return new Array(5).fill('default')
        },
        validator(v) {
            return v.reduce((a, b) => a && (!b || typeof b == 'string'), true)
        },
    },

    // Array of inline styles for different panel elements
    styles: {
        type: Array,
        default() {
            return [{}, {}, {}, {}, {}]
        },
        validator(v) {
            return v.reduce((a, b) => a && (!b || typeof b == 'object'), true)
        },
    },
})

// Reactive state object containing all component state
const page = reactive({
    classes: [], // Processed CSS classes
    help: null, // Help content reference
    isVisibleDock: props.modelValue, // Controls dock visibility
    isVisibleSections: [], // Controls individual section visibility
    isShifted: false, // Whether panel is in shifted state (dual panel mode)
    startTouch: null, // Touch start position for gesture detection
    styles: [], // Processed inline styles
})

// Watch for changes in modelValue prop to control panel visibility
watch(
    () => props.modelValue,
    async () => {
        page.isVisibleDock = props.modelValue
        if (props.modelValue) {
            // Panel is opening
            if (State.app && !page.help) {
                page.help = State.app.help
            }
            if (props.keyboard) {
                // Add event listeners for keyboard and touch interactions
                window.addEventListener('keydown', keyboard, true)
                window.addEventListener('touchstart', touchStart, false)
                window.addEventListener('touchend', touchEnd, false)
            }
        } else {
            // Panel is closing - remove event listeners
            if (props.keyboard) {
                window.removeEventListener('keydown', keyboard, true)
                window.removeEventListener('touchstart', touchStart, false)
                window.removeEventListener('touchend', touchEnd, false)
            }
        }
        // Update global app state
        if (State.app.mobile) {
            State.app.back = page.isVisibleDock ? true : null
        }
    }
)

// Watch for changes in app back state to close panel
watch(
    () => State.app.back,
    async () => {
        // Back pressed by navbar back button
        if (State.app.back == false && State.app.mobile) {
            await closeAll()
        }
    }
)

// Watch for route changes to auto-close panel
watch(
    () => getRoutePath(),
    async () => {
        await closeAll()
    }
)

// Template refs for DOM elements
const dock = ref(null) // Main dock container
const section = ref(null) // Panel sections array

// Emit events
const emit = defineEmits(['close'])

// Lifecycle hooks
onBeforeMount(() => {
    init()
})

onUnmounted(async () => {
    await closeAll()
    window.removeEventListener('keydown', keyboard)
})

// Helper functions

/**
 * Determines if the close all button should be visible
 * Only visible in dual panel mode when not shifted
 */
function isVisibleCloseAll() {
    return props.count == 2 && !page.isShifted
}

/**
 * Gets the current panel count
 */
function getCount() {
    return props.count
}

/**
 * Initializes the component state and processes props
 * Sets up styles, classes, and panel visibility
 */
function init() {
    page.isShifted = false
    page.styles = []
    page.classes = []

    // Process CSS classes for each element (5 total: dock, section1, section2, close, extra)
    for (let i = 0; i < 5; i++) {
        if (props.classes[i] && props.classes[i].length >= 0) {
            // Handle 'same' class - copies from previous element
            i == 2 && props.classes[i] == 'same'
                ? page.classes.push(props.classes[i - 1])
                : page.classes.push(props.classes[i])
        } else {
            page.classes.push('default')
        }
    }

    // Add border classes for dual panel mode
    if (props.count == 2) {
        page.classes[1] += ' vsp-br' // Border right for first panel
        page.classes[2] += ' vsp-bl' // Border left for second panel
    }

    // Process inline styles for each element
    for (let i = 0; i < 5; i++) {
        i == 2 && props.styles[i] && props.styles[i].same
            ? page.styles.push(Object.assign({}, props.styles[i - 1] || {}))
            : page.styles.push(props.styles[i] || {})
    }

    // Handle width calculations and responsive design
    let isSecondSet = false
    let maxWidth = window.innerWidth

    if (props.count == 1) {
        // Single panel - ensure width doesn't exceed screen width
        if (!/\d+%/.test(props.widths[0])) {
            props.widths[0] = `${Math.min(parseInt(props.widths[0]), maxWidth)}px`
        }
    } else if (/\d+%/.test(props.widths[1])) {
        // Dual panel with percentage width - calculate complementary width
        page.styles[2].width = `${100 - props.widths[1].slice(0, -1)}%`
        isSecondSet = true
    }

    // Apply widths to elements
    props.widths.forEach((_w, i) => {
        if (/col-(\w+-)?\d+/.test(_w)) {
            // CSS Grid column class
            page.classes[i] += ' ' + _w
        } else {
            // Direct width value
            if (i == 2 && isSecondSet) return
            page.styles[i].width = _w
        }
    })

    // Initialize section visibility array
    page.isVisibleSections = new Array(props.count).fill(1)
}

/**
 * Handles keyboard events (ESC to close)
 */
async function keyboard(event) {
    if (event.code === 'Escape' && props.keyboard) {
        await closeAll()
    }
}

/**
 * Handles touch start for gesture detection
 */
function touchStart(event) {
    page.startTouch = event.changedTouches[0]
}

/**
 * Handles touch end for gesture detection
 * Supports swipe right to close
 */
function touchEnd(event) {
    let x = event.changedTouches[0].screenX
    let y = event.changedTouches[0].screenY
    if (page.startTouch) {
        let deltaX = Math.abs(x - page.startTouch.screenX)
        let deltaY = Math.abs(y - page.startTouch.screenY)
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX > 50) {
                // Navigate back on swipe right
                go(null)
            } else if (deltaX < -50) {
                // Future: handle swipe left
            }
        }
    }
}

/**
 * Gets processed styles for a specific element by index
 */
function makeStyles(index) {
    return page.styles[index]
}

/**
 * Gets processed CSS classes for a specific element by index
 */
function makeClasses(index) {
    return page.classes[index]
}

/**
 * Applies CSS transforms to an element with animation
 * @param {HTMLElement} el - Target element
 * @param {Array} v - Array containing [transform, timing, duration]
 */
function setTransform(el, v) {
    // Apply CSS transforms with cross-browser compatibility
    ;['webkitTransform', 'MozTransform', 'msTransform', 'OTransform', 'transform'].forEach((t) => {
        el.style[t] = v[0]
    })
    el.style.transitionTimingFunction = v[1]
    el.style.transitionDuration = v[2]
}

/**
 * Shifts the panel to the right to reveal the second panel
 * Used in dual panel mode
 */
function shift() {
    let cx = -section.value[1].offsetWidth
    setTransform(dock.value, ['right', Animation, Timeout])
    dock.value.style.right = `${cx}px`
    page.isShifted = true
}

/**
 * Transition hook called before a section leaves (gets hidden)
 * Handles visual effects and positioning for dual panel mode
 */
function beforeLeave() {
    if (page.isVisibleSections[1]) {
        // Second panel is visible - adjust shadows
        dock.value.style.boxShadow = 'none'
        section.value[1].style.boxShadow = '-3px 3px 9px rgba(0, 0, 0, 0.3)'
    } else {
        // Only first panel visible - shift to reveal second
        shift()
    }
}

/**
 * Transition hook called after a section leaves (gets hidden)
 * Adjusts the dock size and styling
 */
function afterLeave() {
    if (page.isVisibleSections[1]) {
        // Restore dock shadow and adjust width
        dock.value.style.boxShadow = '-3px 3px 9px rgba(0, 0, 0, 0.3)'
        dock.value.style.width = section.value[0].offsetWidth + 'px'
        section.value[0].style.width = '100%'
    }
}

/**
 * Transition hook called when the panel enters (becomes visible)
 * Automatically shifts to second panel if dual mode and extra not shown
 */
function enter() {
    props.count == 2 && !props.showExtra && shift()
}

/**
 * Expands the panel back to full view from shifted state
 * Resets the positioning and removes shift state
 */
function expand() {
    setTransform(dock.value, ['right', Animation, Timeout])
    setTimeout(() => {
        // Remove transforms after animation completes
        setTransform(dock.value, ['', '', ''])
    }, 800)
    dock.value.style.right = '0'
    page.isShifted = false
}

/**
 * Closes the entire dock panel
 * Resets state and emits close event
 */
function closeDock() {
    if (!page.isVisibleDock) {
        return
    }
    page.isVisibleDock = false
    if (!props.nested && State.app.mobile) {
        State.app.back = page.isVisibleDock ? true : null
    }
    init()
    if (State.app) {
        if (!props.isHelp) {
            State.app.setHelp(page.help)
        }
        page.help = null
    }
    emit('close')
}

/**
 * Closes a specific panel section or the entire dock
 * @param {number} index - Index of the section to close (0 or 1)
 */
async function close(index) {
    try {
        // Check for confirmation before closing
        if (props.confirm && !(await props.confirm())) {
            return
        }
    } catch (err) {
        console.log(`Exception in confirm`, err.message)
        return
    }

    if (props.count == 1) {
        // Single panel mode - close entire dock
        closeDock()
    } else if (page.isShifted) {
        // Dual panel mode in shifted state - close with animation
        setTransform(dock.value, ['right', easeOutSine, '1.2s'])
        closeDock()
    } else if (index == 0) {
        // Close first panel in dual mode
        page.isVisibleSections[0] = 0
    } else if (index == 1 && page.isVisibleSections[0]) {
        // Close second panel - shift to first
        shift()
    } else {
        // Default case - close entire dock
        closeDock()
    }
}

/**
 * Closes all panels and the entire dock
 * Handles confirmation and smooth closing animation
 */
async function closeAll() {
    if (props.confirm && page.isVisibleDock && !(await props.confirm('all'))) {
        return
    }
    if (page.isShifted) {
        // Apply closing animation if shifted
        setTransform(dock.value, ['right', easeOutSine, '1.2ss'])
    }
    closeDock()
}
</script>

<template>
    <div>
        <!-- Blackout overlay that appears behind the panel -->
        <transition name="fade">
            <div v-if="page.isVisibleDock" class="blackout" @click.stop.prevent="closeAll"></div>
        </transition>

        <!-- Main panel dock container with slide-out animation -->
        <transition name="slide-out" v-on:enter="enter">
            <div
                v-if="page.isVisibleDock"
                ref="dock"
                class="dock"
                :style="makeStyles(0)"
                :class="makeClasses(0)">
                <!-- Panel sections with transition group for smooth animations -->
                <transition-group
                    name="slide-out"
                    v-on:before-leave="beforeLeave"
                    v-on:afterLeave="afterLeave">
                    <!-- Individual panel sections -->
                    <section
                        v-for="(isVisible, i) in page.isVisibleSections"
                        ref="section"
                        class="page panel"
                        :key="'k' + i"
                        :style="makeStyles(i + 1)"
                        :class="makeClasses(i + 1)">
                        <!-- First panel content slot -->
                        <div v-if="i == 0">
                            <slot></slot>
                        </div>

                        <!-- Second panel content slot for dual mode -->
                        <div v-else>
                            <slot name="extra"></slot>
                        </div>

                        <!-- Close button for each section -->
                        <a
                            href="#"
                            class="action-close"
                            :style="makeStyles(3)"
                            :class="makeClasses(3)"
                            @click.prevent="close(i)"
                            v-html="closeHtml"></a>

                        <!-- Close all button (visible in dual mode when not shifted) -->
                        <a
                            v-if="count == 2 && !page.isShifted && i == 0"
                            href="#"
                            class="action-extra"
                            :style="makeStyles(4)"
                            :class="makeClasses(4)"
                            @click.prevent="closeAll"
                            v-html="closeAllHtml"></a>

                        <!-- Expand button (visible when shifted to restore full view) -->
                        <a
                            v-if="page.isShifted && i == 0"
                            href="#"
                            class="action-extra"
                            :style="makeStyles(4)"
                            :class="makeClasses(4)"
                            @click.prevent="expand"
                            v-html="extraHtml"></a>
                    </section>
                </transition-group>
            </div>
        </transition>
    </div>
</template>

<style scoped>
/* Slide Out Panel Component Styles */
/* Semi-transparent overlay that covers the entire viewport */
.blackout {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 2000;
    background-color: rgba(0, 0, 0, 0.3);
}

/* Main panel dock container positioned on the right side */
.dock {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    z-index: 3000;
    box-shadow: -3px 3px 9px rgba(0, 0, 0, 0.3);
    overflow: hidden;
}

/* Individual panel sections within the dock */
section {
    position: relative;
    display: inline-block;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
}

/* Default dock styling with top padding for navigation */
.dock.default {
    padding-top: 48px;
    z-index: 199 !important;
    .dock.default {
        padding-top: 0 !important;
    }
}

/* Content area within each panel section */
.panel > div {
    height: 100%;
    overflow: auto;
}

/* Default panel styling with theme colors and responsive padding */
.panel.default {
    width: 100%;
    background-color: rgb(var(--v-theme-surface));
    border-radius: 0 !important;
    color: rgb(var(--v-theme-text));
    margin: 0 !important;
    padding: 1rem 1rem 1rem;
    padding-top: 16px;
    .page {
        border-radius: 0 !important;
    }
}

/* Close button positioning - bottom left of panel */
.action-close.default {
    position: absolute;
    bottom: 1rem;
    cursor: pointer;
}

/* Extra/expand button positioning - bottom right of panel */
.action-extra.default {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    cursor: pointer;
}

/* Border styles for dual panel mode */
.vsp-br {
    border-right: 2px solid rgba(0, 0, 0, 0.2);
}

.vsp-bl {
    border-left: 2px solid rgba(0, 0, 0, 0.1);
}

/* Slide-out animation transitions */
.slide-out-enter-active,
.slide-out-leave-active {
    transition: transform 0.8s cubic-bezier(0.215, 0.61, 0.355, 1);
}

.slide-out-enter,
.slide-out-leave-to {
    transform: translateX(100%);
}

/* Fade animation transitions for blackout overlay */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.8s ease-in;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Utility class for transparent backgrounds */
.bg-transparent {
    background-color: transparent !important;
}
</style>

<style lang="scss">
/* Additional SCSS styles for full-screen dock mode */
.dock.full {
    .panel.default {
        /* Mobile responsive padding for full-screen mode */
        @media (max-width: 640px) {
            padding-top: 16px !important;
        }
    }
}
</style>
